import React from 'react';
import { parseMarkdownSummary, hasMarkdownFormatting } from '../../utils/markdown';
import { getTextDirection } from '../../utils/language';
import HighlightRenderer from '../HighlightRenderer';
import ImageDebugPanel from '../ImageDebugPanel';
import RelatedArticlesWidget from '../RelatedArticlesWidget';
import { FeedbackButtons, MarkReadButton } from './ArticleControls';

export const SummaryTab = ({
  article,
  articleId,
  highlights,
  onHighlightClick,
  isRegenerating,
  onRegenerateSummary,
  feedback,
  feedbackLoading,
  onFeedback,
  isUpdating,
  onMarkAsRead,
  onMarkAsReadStay,
  isOffline,
}) => (
  <div className="card-clay-static !px-3 !py-4 sm:!p-8">
    {article.summary ? (
      <div>
        {/* Re-summarize button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={onRegenerateSummary}
            disabled={isRegenerating || isOffline}
            className="btn-clay-ghost inline-flex items-center"
            title={isOffline ? "Connect to internet to do this" : "Generate a new summary for this article"}
          >
            {isRegenerating ? (
              <>
                <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                Regenerating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Re-summarize
              </>
            )}
          </button>
        </div>

        {/* Summary content with highlight overlay */}
        <HighlightRenderer
          text={null}
          highlights={highlights}
          onHighlightClick={onHighlightClick}
        >
          <div
            className="prose prose-lg max-w-none"
            dir={getTextDirection(article.language)}
            data-markdown-source={article.summary}
            data-markdown={article.summary}
          >
            {hasMarkdownFormatting(article.summary) ? (
              <div className="space-y-2">
                {parseMarkdownSummary(article.summary)}
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-body-text leading-relaxed">
                {article.summary}
              </div>
            )}
          </div>
        </HighlightRenderer>

        {/* Image Debug Panel (dev only) */}
        {article.debug_data && (
          <ImageDebugPanel
            debugData={article.debug_data}
            threshold={0.08}
          />
        )}

        <FeedbackButtons feedback={feedback} feedbackLoading={feedbackLoading} onFeedback={onFeedback} />
        <MarkReadButton isRead={article.is_read} isUpdating={isUpdating} onMarkAsRead={onMarkAsRead} onMarkAsReadStay={onMarkAsReadStay} />

        <RelatedArticlesWidget
          articleId={articleId}
          currentArticle={article}
        />
      </div>
    ) : (
      <div>
        <div className="text-center py-12">
          <div className="text-5xl mb-4" role="img" aria-label="No summary">📝</div>
          <p className="font-display text-lg font-medium text-body-text mb-4">No summary available yet</p>
          {article.status === 'processing' ? (
            <p className="text-sm text-secondary-text mt-2">Summary will be generated shortly...</p>
          ) : (
            <button
              onClick={onRegenerateSummary}
              disabled={isRegenerating || isOffline}
              className="btn-clay-accent inline-flex items-center"
              title={isOffline ? "Connect to internet to do this" : undefined}
            >
              {isRegenerating ? (
                <>
                  <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Generate Summary
                </>
              )}
            </button>
          )}
        </div>
        <FeedbackButtons feedback={feedback} feedbackLoading={feedbackLoading} onFeedback={onFeedback} />
        <MarkReadButton isRead={article.is_read} isUpdating={isUpdating} onMarkAsRead={onMarkAsRead} onMarkAsReadStay={onMarkAsReadStay} />
      </div>
    )}
  </div>
);

export const ContentTab = ({
  article,
  articleId,
  isRefetching,
  onRefetchContent,
  feedback,
  feedbackLoading,
  onFeedback,
  isUpdating,
  onMarkAsRead,
  onMarkAsReadStay,
  isOffline,
}) => (
  <div className="card-clay-static !px-3 !py-4 sm:!p-8">
    {article.content ? (
      <div>
        {/* Re-fetch content button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={onRefetchContent}
            disabled={isRefetching || isOffline}
            className="btn-clay-ghost inline-flex items-center"
            title={isOffline ? "Connect to internet to do this" : "Re-fetch the latest content from the original URL"}
          >
            {isRefetching ? (
              <>
                <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                Re-fetching...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Re-fetch Content
              </>
            )}
          </button>
        </div>

        <div
          className="prose prose-lg max-w-none"
          dir={getTextDirection(article.language)}
          data-markdown-source={article.content}
          data-markdown={article.content}
        >
          {hasMarkdownFormatting(article.content) ? (
            <div className="space-y-2">
              {parseMarkdownSummary(article.content)}
            </div>
          ) : (
            <div className="whitespace-pre-wrap text-body-text leading-relaxed">
              {article.content}
            </div>
          )}
        </div>

        <FeedbackButtons feedback={feedback} feedbackLoading={feedbackLoading} onFeedback={onFeedback} />
        <MarkReadButton isRead={article.is_read} isUpdating={isUpdating} onMarkAsRead={onMarkAsRead} onMarkAsReadStay={onMarkAsReadStay} />

        <RelatedArticlesWidget
          articleId={articleId}
          currentArticle={article}
        />
      </div>
    ) : (
      <div>
        <div className="text-center py-12">
          <div className="text-5xl mb-4" role="img" aria-label="No content">📖</div>
          <p className="font-display text-lg font-medium text-body-text mb-4">No content available yet</p>
          {article.status === 'processing' ? (
            <p className="text-sm text-secondary-text mt-2">Content is being extracted...</p>
          ) : (
            <button
              onClick={onRefetchContent}
              disabled={isRefetching || isOffline}
              className="btn-clay-accent inline-flex items-center"
              title={isOffline ? "Connect to internet to do this" : undefined}
            >
              {isRefetching ? (
                <>
                  <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                  Fetching...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Fetch Content
                </>
              )}
            </button>
          )}
        </div>
        <FeedbackButtons feedback={feedback} feedbackLoading={feedbackLoading} onFeedback={onFeedback} />
        <MarkReadButton isRead={article.is_read} isUpdating={isUpdating} onMarkAsRead={onMarkAsRead} onMarkAsReadStay={onMarkAsReadStay} />
      </div>
    )}
  </div>
);

export const OriginalTab = ({ article }) => (
  <div className="card-clay-static !px-3 !py-4 sm:!p-8">
    <div className="text-center">
      <div className="mb-6">
        <div className="text-5xl mb-4" role="img" aria-label="External link">🔗</div>
        <h3 className="font-display text-xl font-bold text-heading mb-2">
          View Original Article
        </h3>
        <p className="text-secondary-text mb-6">
          Visit the original source to read the full article with all formatting, images, and interactive elements.
        </p>
      </div>

      <a
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-clay-accent inline-flex items-center text-base"
      >
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        Open Original Article
      </a>

      <div className="mt-4 text-sm text-secondary-text bg-surface-alt/50 p-3 rounded-clay border-2 border-theme-border-subtle">
        <span className="break-all">{article.url}</span>
      </div>
    </div>
  </div>
);
